import React, { useState, useRef, useEffect, useCallback } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { authService } from './services/api';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: {
        sitekey: string;
        theme?: 'light' | 'dark' | 'auto';
        callback?: (token: string) => void;
        'error-callback'?: () => void;
        'expired-callback'?: () => void;
      }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

interface LoginProps {
  onLogin: (user: any) => void;
}

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  // MFA states
  const [mfaStep, setMfaStep] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  // Password reset states
  const [forgotStep, setForgotStep] = useState<'idle' | 'form' | 'sent' | 'reset'>('idle');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // MFA forced setup states
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [mfaSetupStep, setMfaSetupStep] = useState<'qr' | 'backup'>('qr');
  const [mfaQrDataUrl, setMfaQrDataUrl] = useState('');
  // DOIS tokens diferentes, de propósitos diferentes:
  //  - totpSetupToken: devolvido por /mfa/setup, carrega o segredo do TOTP e vai
  //    no BODY do /mfa/setup/verify.
  //  - mfaGateToken: o `mfaSetupToken` que o /login emite quando o usuário ainda
  //    não tem 2º fator (DEM-95). É identidade, vai no header Authorization, e
  //    não existe quando se fala com o backend antigo.
  const [totpSetupToken, setTotpSetupToken] = useState('');
  const [mfaGateToken, setMfaGateToken] = useState('');
  const [mfaSetupCode, setMfaSetupCode] = useState('');
  const [mfaBackupCodes, setMfaBackupCodes] = useState<string[]>([]);
  const [pendingLoginResponse, setPendingLoginResponse] = useState<any>(null);

  // Passkey states
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeySupported, setPasskeySupported] = useState(false);

  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const mfaInputRef = useRef<HTMLInputElement>(null);

  const renderTurnstile = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current || !window.turnstile) return;
    if (widgetIdRef.current !== null) return;

    widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: 'dark',
      callback: (token: string) => setTurnstileToken(token),
      'error-callback': () => setTurnstileToken(''),
      'expired-callback': () => setTurnstileToken(''),
    });
  }, []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    // If turnstile script is already loaded
    if (window.turnstile) {
      renderTurnstile();
      return;
    }

    // Wait for the script to load
    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval);
        renderTurnstile();
      }
    }, 200);

    return () => clearInterval(interval);
  }, [renderTurnstile]);

  // Detect ?reset_token= in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset_token');
    if (token) {
      setResetToken(token);
      setForgotStep('reset');
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Detect passkey support
  useEffect(() => {
    if (window.PublicKeyCredential) {
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.()
        .then((available) => setPasskeySupported(available))
        .catch(() => setPasskeySupported(false));
    }
  }, []);

  const handlePasskeyLogin = async () => {
    // O e-mail deixou de ser obrigatório: sem ele o navegador mostra as passkeys
    // que já tem para este site e o próprio autenticador identifica quem é você.
    // Se o campo estiver preenchido, mandamos junto — restringe a escolha ao dono
    // daquele e-mail e mantém o fluxo antigo funcionando.
    const passkeyEmail = email.trim() || undefined;
    setError('');
    setPasskeyLoading(true);
    try {
      const options = await authService.webauthnAuthenticateBegin(passkeyEmail);
      const authResp = await startAuthentication({ optionsJSON: options });
      const response = await authService.webauthnAuthenticateComplete(passkeyEmail, JSON.stringify(authResp));
      localStorage.setItem('user', JSON.stringify(response));
      onLogin(response);
    } catch (err: any) {
      console.error('Erro no login via Passkey:', err);
      if (err.name === 'NotAllowedError') {
        setError('Autenticacao cancelada ou nao permitida');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Erro ao autenticar com Passkey');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authService.forgotPassword(forgotEmail);
      setForgotStep('sent');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao enviar email');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) { setError('As senhas nao coincidem'); return; }
    if (newPassword.length < 8) { setError('Minimo 8 caracteres'); return; }
    if (!/[A-Z]/.test(newPassword)) { setError('Precisa de letra maiuscula'); return; }
    if (!/[a-z]/.test(newPassword)) { setError('Precisa de letra minuscula'); return; }
    if (!/\d/.test(newPassword)) { setError('Precisa de numero'); return; }
    setLoading(true);
    try {
      await authService.resetPassword(resetToken, newPassword);
      setResetSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao redefinir senha');
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login(email, password, turnstileToken || undefined);

      // MFA required — show TOTP input
      if (response.mfaRequired) {
        setMfaToken(response.mfaToken);
        setMfaStep(true);
        setMfaCode('');
        setLoading(false);
        setTimeout(() => mfaInputRef.current?.focus(), 100);
        return;
      }

      // MFA setup required — user must configure MFA before accessing system
      if (response.mfaSetupRequired) {
        // Compatível com os dois backends: o novo devolve `mfaSetupToken` e
        // NENHUMA credencial de sessão; o antigo devolve a sessão inteira e
        // nenhum token de escopo (aí o setupMfa vai pela sessão, como sempre).
        const gate: string = response.mfaSetupToken || '';
        setMfaGateToken(gate);
        setPendingLoginResponse(response);
        setLoading(true);
        try {
          const setupData = await authService.setupMfa(gate || undefined);
          setMfaQrDataUrl(setupData.qrDataUrl);
          setTotpSetupToken(setupData.setupToken);
          setMfaSetupRequired(true);
          setMfaSetupStep('qr');
          setMfaSetupCode('');
        } catch (setupErr: any) {
          console.error('Erro ao iniciar setup MFA:', setupErr);
          setError('Erro ao configurar autenticacao de dois fatores. Tente novamente.');
        }
        setLoading(false);
        return;
      }

      localStorage.setItem('user', JSON.stringify(response));
      onLogin(response);
    } catch (err: any) {
      console.error('Erro no login:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message === 'Network Error') {
        setError('Erro de conexão. Verifique sua internet.');
      } else {
        setError('Email ou senha incorretos');
      }
      // Reset Turnstile widget after error (token is single-use)
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        setTurnstileToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.verifyMfa(mfaToken, mfaCode.trim(), rememberDevice);
      localStorage.setItem('user', JSON.stringify(response));
      onLogin(response);
    } catch (err: any) {
      console.error('Erro na verificação MFA:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Código inválido. Tente novamente.');
      }
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaBack = () => {
    setMfaStep(false);
    setMfaToken('');
    setMfaCode('');
    setError('');
    setUseBackupCode(false);
  };

  const handleMfaSetupVerify = async () => {
    if (mfaSetupCode.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      const data = await authService.verifyMfaSetup(
        totpSetupToken,
        mfaSetupCode,
        mfaGateToken || undefined,
      );
      setMfaBackupCodes(data.backupCodes);
      // Backend novo devolve a sessão aqui (o authService já adotou o access
      // token). Backend antigo não devolve — aí vale a resposta do login.
      if (data.access_token) setPendingLoginResponse(data);
      setMfaSetupStep('backup');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Codigo invalido. Tente novamente.');
    }
    setLoading(false);
  };

  const handleMfaSetupComplete = () => {
    if (pendingLoginResponse) {
      // `pendingLoginResponse` pode ser a resposta do /mfa/setup/verify, que vem
      // com os códigos de backup junto. Eles são credencial de recuperação e não
      // podem ir para o localStorage — o perfil guardado é só para pintar a UI.
      const { success, backupCodes, ...perfil } = pendingLoginResponse;
      localStorage.setItem('user', JSON.stringify(perfil));
      onLogin(perfil);
    }
  };

  const isButtonDisabled = loading || (!!TURNSTILE_SITE_KEY && !turnstileToken);

  return (
    <div className="min-h-screen flex">
      {/* Lado Esquerdo - Azul */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-b from-blue-600 via-blue-700 to-blue-800 p-12 flex-col justify-between relative overflow-hidden">

        <div className="relative z-10">
          {/* Logo com fundo branco */}
          <div className="bg-white rounded-2xl p-4 inline-block mb-10">
            <img src="/ipcom-logo.svg" alt="IPCOM" className="h-16" />
          </div>

          {/* Título */}
          <h1 className="text-4xl font-bold text-white mb-3">IPCOM Hub</h1>
          <p className="text-blue-200 text-lg mb-10">Gestão completa de clientes, contratos e receita recorrente</p>

          {/* Features */}
          <div className="space-y-4">
            <div className="bg-blue-500/30 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4 border border-blue-400/20">
              <div className="bg-orange-500 p-3 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-white font-semibold">Dashboard Completo</h3>
                <p className="text-blue-200 text-sm">Visualize MRR, clientes e contratos em tempo real</p>
              </div>
            </div>

            <div className="bg-blue-500/30 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4 border border-blue-400/20">
              <div className="bg-blue-400 p-3 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-white font-semibold">Gestão de Clientes</h3>
                <p className="text-blue-200 text-sm">Cadastro completo com CNPJ e contatos</p>
              </div>
            </div>

            <div className="bg-blue-500/30 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4 border border-blue-400/20">
              <div className="bg-gray-500 p-3 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-white font-semibold">Contratos e MRR</h3>
                <p className="text-blue-200 text-sm">Controle de receita recorrente mensal</p>
              </div>
            </div>
          </div>

          {/* Botão Conheça nossas soluções */}
          <a
            href="https://ipcom.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center justify-center gap-2 w-full bg-orange-500 text-white py-4 px-6 rounded-xl hover:bg-orange-600 transition-colors font-semibold"
          >
            Conheça nossas soluções
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <div className="bg-blue-500/30 backdrop-blur-sm rounded-xl px-5 py-4 flex items-center gap-4 border border-blue-400/20 inline-block">
            <div className="bg-gray-600 p-2.5 rounded-xl">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">300+</p>
              <p className="text-blue-200 text-sm">Empresas atendidas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Lado Direito - Escuro */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        {/* Efeito de luz laranja no fundo */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 h-48 bg-orange-500/20 blur-3xl rounded-full"></div>

        <div className="w-full max-w-md relative z-10">
          {/* Logo mobile */}
          <div className="lg:hidden flex justify-center mb-8">
            <div className="bg-white rounded-xl p-3 inline-block">
              <img src="/ipcom-logo.svg" alt="IPCOM" className="h-8" />
            </div>
          </div>

          {/* Card de Login */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50">
            {forgotStep !== 'idle' ? (
              <>
                {/* Password Recovery */}
                <div className="flex justify-center mb-6">
                  <div className="bg-blue-500/20 p-4 rounded-full">
                    <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>

                {forgotStep === 'form' && (
                  <>
                    <h2 className="text-2xl font-bold text-center text-white mb-2">Esqueceu a senha?</h2>
                    <p className="text-center text-gray-400 mb-8">Digite seu email para receber o link de recuperacao</p>
                    <form onSubmit={handleForgotPassword} className="space-y-5">
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <input type="email" value={forgotEmail} onChange={e => { setForgotEmail(e.target.value); setError(''); }} placeholder="Seu email" className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" autoFocus required />
                      </div>
                      {error && (
                        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-xl text-sm">{error}</div>
                      )}
                      <button type="submit" disabled={loading} className="w-full bg-primary text-white py-3.5 rounded-xl hover:bg-primary-strong font-semibold disabled:opacity-50">
                        {loading ? 'Enviando...' : 'Enviar link'}
                      </button>
                      <button type="button" onClick={() => { setForgotStep('idle'); setError(''); }} className="w-full text-gray-400 hover:text-white text-sm py-2">Voltar ao login</button>
                    </form>
                  </>
                )}

                {forgotStep === 'sent' && (
                  <>
                    <h2 className="text-2xl font-bold text-center text-white mb-2">Email enviado!</h2>
                    <p className="text-center text-gray-400 mb-6">Se o email <strong className="text-white">{forgotEmail}</strong> estiver cadastrado, voce recebera um link para redefinir sua senha.</p>
                    <p className="text-center text-gray-500 text-sm mb-6">Verifique tambem a caixa de spam. O link expira em 30 minutos.</p>
                    <button onClick={() => { setForgotStep('idle'); setError(''); }} className="w-full bg-primary text-white py-3.5 rounded-xl hover:bg-primary-strong font-semibold">Voltar ao login</button>
                  </>
                )}

                {forgotStep === 'reset' && !resetSuccess && (
                  <>
                    <h2 className="text-2xl font-bold text-center text-white mb-2">Nova senha</h2>
                    <p className="text-center text-gray-400 mb-8">Defina sua nova senha de acesso</p>
                    <form onSubmit={handleResetPassword} className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Nova senha</label>
                        <input type="password" value={newPassword} onChange={e => { setNewPassword(e.target.value); setError(''); }} placeholder="Minimo 8 caracteres" className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" autoFocus required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Confirmar senha</label>
                        <input type="password" value={confirmPassword} onChange={e => { setConfirmPassword(e.target.value); setError(''); }} placeholder="Repita a senha" className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
                        {confirmPassword && newPassword !== confirmPassword && <p className="mt-1 text-xs text-red-400">As senhas nao coincidem</p>}
                      </div>
                      {error && (
                        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-xl text-sm">{error}</div>
                      )}
                      <button type="submit" disabled={loading || !newPassword || newPassword !== confirmPassword} className="w-full bg-primary text-white py-3.5 rounded-xl hover:bg-primary-strong font-semibold disabled:opacity-50">
                        {loading ? 'Salvando...' : 'Redefinir senha'}
                      </button>
                    </form>
                  </>
                )}

                {forgotStep === 'reset' && resetSuccess && (
                  <>
                    <div className="flex justify-center mb-4">
                      <div className="bg-green-500/20 p-4 rounded-full">
                        <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <h2 className="text-2xl font-bold text-center text-white mb-2">Senha redefinida!</h2>
                    <p className="text-center text-gray-400 mb-6">Sua senha foi alterada com sucesso. Faca login com a nova senha.</p>
                    <button onClick={() => { setForgotStep('idle'); setResetSuccess(false); setError(''); }} className="w-full bg-success text-white py-3.5 rounded-xl hover:bg-success-strong font-semibold">Ir para o login</button>
                  </>
                )}
              </>
            ) : mfaSetupRequired ? (
              <>
                {/* Forced MFA Setup */}
                <div className="flex justify-center mb-6">
                  <div className="bg-orange-500/20 p-4 rounded-full">
                    <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                </div>

                {mfaSetupStep === 'qr' ? (
                  <>
                    <h2 className="text-2xl font-bold text-center text-white mb-2">Configurar autenticacao</h2>
                    <p className="text-center text-gray-400 mb-6">Para sua seguranca, configure a verificacao em duas etapas antes de continuar.</p>

                    <div className="space-y-5">
                      <div className="flex justify-center bg-white rounded-xl p-4 mx-auto w-fit">
                        <img src={mfaQrDataUrl} alt="QR Code MFA" className="w-48 h-48" />
                      </div>

                      <p className="text-center text-gray-500 text-sm">Escaneie com Google Authenticator, Authy ou similar</p>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Digite o codigo de 6 digitos:</label>
                        <input
                          type="text"
                          value={mfaSetupCode}
                          onChange={e => { setMfaSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                          placeholder="000000"
                          maxLength={6}
                          inputMode="numeric"
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent text-center text-2xl tracking-[0.3em] font-mono"
                          autoFocus
                          autoComplete="one-time-code"
                        />
                      </div>

                      {error && (
                        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {error}
                        </div>
                      )}

                      <button
                        onClick={handleMfaSetupVerify}
                        disabled={loading || mfaSetupCode.length !== 6}
                        className="w-full bg-primary text-white py-3.5 rounded-xl hover:bg-primary-strong transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Verificando...' : 'Ativar e continuar'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-center text-white mb-2">Salve seus codigos</h2>
                    <p className="text-center text-gray-400 mb-6">Guarde estes codigos de backup em um lugar seguro. Eles so serao exibidos uma vez.</p>

                    <div className="space-y-5">
                      <div className="grid grid-cols-2 gap-2">
                        {mfaBackupCodes.map((code, i) => (
                          <div key={i} className="bg-gray-700/50 px-3 py-2 rounded-lg text-center font-mono text-sm text-white tracking-wider">{code}</div>
                        ))}
                      </div>

                      <button
                        onClick={() => { navigator.clipboard.writeText(mfaBackupCodes.join('\n')); alert('Codigos copiados!'); }}
                        className="w-full py-2.5 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700/50 text-sm"
                      >
                        Copiar codigos
                      </button>

                      <button
                        onClick={handleMfaSetupComplete}
                        className="w-full bg-success text-white py-3.5 rounded-xl hover:bg-success-strong transition-colors font-semibold"
                      >
                        Pronto, salvei os codigos
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : mfaStep ? (
              <>
                {/* MFA Verification Step */}
                <div className="flex justify-center mb-6">
                  <div className="bg-orange-500/20 p-4 rounded-full">
                    <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-center text-white mb-2">Verificacao em duas etapas</h2>
                <p className="text-center text-gray-400 mb-8">
                  {useBackupCode
                    ? 'Digite um dos seus codigos de backup'
                    : 'Digite o codigo de 6 digitos do seu aplicativo autenticador'
                  }
                </p>

                <form onSubmit={handleMfaSubmit} className="space-y-5">
                  <div>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <input
                        ref={mfaInputRef}
                        type="text"
                        value={mfaCode}
                        onChange={(e) => { setMfaCode(e.target.value.replace(useBackupCode ? /[^A-Za-z0-9-]/g : /\D/g, '')); setError(''); }}
                        placeholder={useBackupCode ? 'XXXX-XXXX' : '000000'}
                        maxLength={useBackupCode ? 9 : 6}
                        inputMode={useBackupCode ? 'text' : 'numeric'}
                        className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-center text-2xl tracking-[0.3em] font-mono"
                        autoFocus
                        autoComplete="one-time-code"
                        required
                      />
                    </div>
                  </div>

                  <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={rememberDevice}
                      onChange={(e) => setRememberDevice(e.target.checked)}
                      className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-orange-500 focus:ring-orange-500"
                    />
                    <span>
                      Confiar neste dispositivo por 30 dias
                      <span className="block text-xs text-gray-500">
                        Nao pede o codigo de novo neste navegador. Use apenas em dispositivo pessoal.
                      </span>
                    </span>
                  </label>

                  {error && (
                    <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || (!useBackupCode && mfaCode.length !== 6) || (useBackupCode && mfaCode.length < 8)}
                    className="w-full bg-primary text-white py-3.5 rounded-xl hover:bg-primary-strong transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Verificando...
                      </span>
                    ) : 'Verificar'}
                  </button>

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleMfaBack}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Voltar ao login
                    </button>
                    <button
                      type="button"
                      onClick={() => { setUseBackupCode(!useBackupCode); setMfaCode(''); setError(''); }}
                      className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      {useBackupCode ? 'Usar aplicativo' : 'Usar codigo de backup'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                {/* Normal Login Step */}
                <div className="flex justify-center mb-6">
                  <div className="bg-blue-500/20 p-4 rounded-full">
                    <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-center text-white mb-2">Bem-vindo de volta</h2>
                <p className="text-center text-gray-400 mb-8">Entre com suas credenciais para acessar sua conta</p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    {/* O backend autentica só por e-mail (findOne({ where: { email } })).
                        O rótulo antigo prometia "nome de usuário", que nunca funcionou. */}
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      E-mail
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(''); }}
                        placeholder="Seu email"
                        className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        autoFocus
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-300">Senha</label>
                      <button type="button" onClick={() => { setForgotStep('form'); setForgotEmail(email); setError(''); }} className="text-sm text-blue-400 hover:text-blue-300">Esqueceu a senha?</button>
                    </div>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(''); }}
                        placeholder="Sua senha"
                        className="w-full pl-10 pr-12 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                      >
                        {showPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Cloudflare Turnstile */}
                  {TURNSTILE_SITE_KEY && (
                    <div className="flex justify-center">
                      <div ref={turnstileRef} />
                    </div>
                  )}

                  {error && (
                    <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isButtonDisabled}
                    className="w-full bg-primary text-white py-3.5 rounded-xl hover:bg-primary-strong transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Entrando...
                      </span>
                    ) : 'Entrar'}
                  </button>

                  {passkeySupported && (
                    <>
                      <div className="flex items-center gap-3 my-1">
                        <div className="flex-1 h-px bg-gray-700"></div>
                        <span className="text-gray-500 text-xs">ou</span>
                        <div className="flex-1 h-px bg-gray-700"></div>
                      </div>
                      {/* O botão NÃO depende do e-mail: com o campo vazio o navegador
                          oferece as passkeys que já tem para este site e o autenticador
                          identifica o usuário. Havia um `!email` aqui que deixava o botão
                          inerte e fazia parecer que o e-mail ainda era obrigatório. */}
                      <button
                        type="button"
                        onClick={handlePasskeyLogin}
                        disabled={passkeyLoading}
                        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border border-gray-600 text-white hover:bg-gray-700/50 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {passkeyLoading ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Verificando...
                          </span>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                            </svg>
                            Entrar com Passkey
                          </>
                        )}
                      </button>
                    </>
                  )}
                </form>
              </>
            )}
          </div>

          {/* Indicadores */}
          <div className="flex justify-center gap-2 mt-6">
            <div className="w-2 h-2 rounded-full bg-orange-500"></div>
            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
          </div>

          <p className="text-center text-gray-500 text-sm mt-4">
            Sua gestão financeira em um só lugar
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
